import { env } from '@/env';
import type {
  TrackType,
  ACCESSEligibilityResultCode,
  EligibilityTrackStatus,
  CmsGender,
  AlignmentOutcomeCategory,
} from '@/types/registration';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const ELIGIBILITY_URL = '/api/v1/eligibility';
const ALIGNMENT_URL = '/api/v1/alignment';

export interface EligibilitySubmitResponse {
  submissionId: string;
}

export type AdministrativeGender = CmsGender;

export interface EligibilityPatientName {
  family: string;
  given: string[];
}

// Matches the CMS Integration Service POST /api/v1/eligibility body
// (required: mbi, track, patientName, gender, birthDate).
export interface EligibilityRequest {
  mbi: string;
  track: TrackType;
  patientName: EligibilityPatientName;
  gender: AdministrativeGender;
  birthDate: string; // ISO date, YYYY-MM-DD
}

export interface EligibilityResultResponse {
  status: EligibilityTrackStatus | string;
  resultCode?: ACCESSEligibilityResultCode;
}

export interface AlignmentCondition {
  code: string;
  display: string;
  onsetDateTime: string;
}

export interface AlignmentRequest {
  track: TrackType;
  mbi: string;
  patientName: { family: string; given: string[] };
  gender: 'male' | 'female' | 'other' | 'unknown';
  birthDate: string;
  conditions: AlignmentCondition[];
  consentVoluntary: boolean;
  consentApiDataSharing: boolean;
  consentPcpSharing: boolean;
  consentControlGroup: boolean;
  consentTimestamp: number;
}

export interface AlignmentSubmitResponse {
  submissionId: string;
}

// GET /api/v1/alignment/:submissionId. The CMS Integration Service returns
// 202 { status } while pending/processing, and 200 { submissionId, status:'complete',
// resultCode, outcome } once terminal. 404/422 surface as CmsApiError (see unwrap).
export interface AlignmentResultResponse {
  submissionId?: string;
  // Closed union (no `| string`): the alignment route only ever emits these
  // statuses, so the poller's status handling stays exhaustive at compile time.
  status: EligibilityTrackStatus;
  resultCode?: string;
  outcome?: AlignmentOutcomeCategory;
}

export class CmsApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'CmsApiError';
    this.status = status;
  }
}

const authConfig = (authToken?: string): AxiosRequestConfig | undefined =>
  authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined;

/**
 * Client for the CMS Integration Service (DACI 2). Kept separate from the
 * hello-server ApiClient in api.ts because it targets a different base URL.
 *
 * NOTE (RND-33880): eligibility runs pre-account-creation, so there is no user
 * auth token yet. `authToken` is optional here; the pre-auth/registration token
 * is resolved when the call is wired in RND-33880.
 */
export class CmsApiClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // Unwrap the response data, but never let a raw AxiosError escape: its
  // config/request/response carry the request payload (MBI, name, DOB, gender =
  // PHI). Surface only a sanitized message + status via CmsApiError so a caller
  // that logs the error cannot leak PHI into logs/analytics.
  private async unwrap<T>(call: Promise<AxiosResponse<T>>): Promise<T> {
    try {
      const response = await call;
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        throw new CmsApiError(err.message, err.response?.status);
      }
      throw err;
    }
  }

  async postEligibility(
    input: EligibilityRequest,
    authToken?: string,
  ): Promise<EligibilitySubmitResponse> {
    return this.unwrap(
      this.axiosInstance.post<EligibilitySubmitResponse>(
        ELIGIBILITY_URL,
        input,
        authConfig(authToken),
      ),
    );
  }

  async getEligibilityResult(
    submissionId: string,
    authToken?: string,
  ): Promise<EligibilityResultResponse> {
    return this.unwrap(
      this.axiosInstance.get<EligibilityResultResponse>(
        `${ELIGIBILITY_URL}/${submissionId}`,
        authConfig(authToken),
      ),
    );
  }

  // Defined for completeness (WR-3). Exercised by RND-33882/RND-33883.
  async postAlignment(
    payload: AlignmentRequest,
    authToken?: string,
  ): Promise<AlignmentSubmitResponse> {
    return this.unwrap(
      this.axiosInstance.post<AlignmentSubmitResponse>(
        ALIGNMENT_URL,
        payload,
        authConfig(authToken),
      ),
    );
  }

  // Poll the result of an alignment submission (RND-36355). 202 resolves with
  // { status: 'pending' | 'processing' }; 200 resolves with the terminal result
  // (status 'complete' + outcome). 404/422 reject as CmsApiError carrying the
  // HTTP status so the poller can distinguish not-found / failed-or-timed-out.
  async getAlignmentResult(
    submissionId: string,
    authToken?: string,
  ): Promise<AlignmentResultResponse> {
    return this.unwrap(
      this.axiosInstance.get<AlignmentResultResponse>(
        `${ALIGNMENT_URL}/${submissionId}`,
        authConfig(authToken),
      ),
    );
  }
}

export const cmsApiClient = new CmsApiClient(env.CMS_SERVICE_URL);
